import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Stethoscope } from "lucide-react";

/**
 * Doctor partner sign-in. No password: a one-time code goes to the phone or email the
 * doctor applied with, and only an approved partner can ask for one.
 */
export default function PartnerLogin() {
  const [contact, setContact] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("contact");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const sendCode = async () => {
    if (!contact.trim()) return toast.error("Enter the mobile number or email you applied with");
    setBusy(true);
    try {
      const { data } = await api.post("/partner/auth/request-code", { contact: contact.trim() });
      setStage("code");
      if (data.dev_code) {
        setCode(data.dev_code);
        toast.success(`Test mode — your code is ${data.dev_code}`);
      } else {
        toast.success(`Code sent by ${data.channel} to ${data.to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const signIn = async () => {
    setBusy(true);
    try {
      await api.post("/partner/auth/verify", { contact: contact.trim(), code: code.trim() });
      await refresh();
      navigate("/partner");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-5 py-20" data-testid="partner-login-page">
      <p className="overline flex items-center gap-2"><Stethoscope size={14} /> Doctor partner</p>
      <h1 className="font-display text-4xl mt-2 mb-3">Sign in to your referrals</h1>
      <p className="text-base text-muted-foreground mb-8">
        We send a one-time code to the mobile number or email your application was made with.
      </p>

      <Card className="border-border">
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="overline">Mobile or email</label>
            <Input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="98765 43210 or you@clinic.com"
              className="mt-2 bg-transparent"
              disabled={stage === "code"}
              data-testid="partner-login-contact"
            />
          </div>

          {stage === "code" && (
            <div>
              <label className="overline">Six-digit code</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                className="mt-2 bg-transparent tracking-[0.4em] text-center font-display text-xl"
                data-testid="partner-login-code"
              />
            </div>
          )}

          {stage === "contact" ? (
            <Button disabled={busy} className="btn-action h-12 w-full" onClick={sendCode} data-testid="partner-send-code">
              <KeyRound size={14} className="mr-1.5" /> {busy ? "Sending…" : "Send me a code"}
            </Button>
          ) : (
            <div className="space-y-2">
              <Button disabled={busy || code.length < 6} className="btn-action h-12 w-full" onClick={signIn}
                      data-testid="partner-verify-code">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
              <Button variant="ghost" className="btn-pill w-full" onClick={() => setStage("contact")}
                      data-testid="partner-change-contact">
                Use a different number or email
              </Button>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Not a partner yet? <a href="/doctor-partner" className="text-primary hover:underline">Apply here</a>.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
